import { Building2, Map, MapPin, FolderClosed, type LucideIcon } from "lucide-react";

// --- Permission Group Types ---

export type PermissionGroupLabel = "tenant" | "state" | "site" | "tag";

export type PermissionGroup = {
  id: string;
  name: string;
  label: PermissionGroupLabel;
  parentId: string | null;
  tenantId: string;
};

// --- Label Configuration ---

export type LabelConfig = {
  icon: LucideIcon;
  color: string;
  bgColor: string;
  displayName: string;
};

export const LABEL_CONFIG: Record<PermissionGroupLabel, LabelConfig> = {
  tenant: { icon: Building2, color: "text-blue-400", bgColor: "bg-blue-500/20", displayName: "Tenant" },
  state: { icon: Map, color: "text-green-400", bgColor: "bg-green-500/20", displayName: "State" },
  site: { icon: MapPin, color: "text-yellow-400", bgColor: "bg-yellow-500/20", displayName: "Site" },
  tag: { icon: FolderClosed, color: "text-purple-400", bgColor: "bg-purple-500/20", displayName: "Group" },
};

// --- Valid Hierarchy Rules ---

export const VALID_CHILD_TYPES: Record<PermissionGroupLabel, PermissionGroupLabel[]> = {
  tenant: ["state", "site", "tag"],
  state: ["site", "tag"],
  site: ["tag"],
  tag: ["tag"],
};

export const VALID_PARENT_LABELS: Record<PermissionGroupLabel, PermissionGroupLabel[]> = {
  tenant: [],
  state: ["tenant"],
  site: ["tenant", "state"],
  tag: ["tenant", "state", "site", "tag"],
};

// --- Utility Functions ---

export const getChildGroups = (groups: PermissionGroup[], parentId: string | null): PermissionGroup[] => {
  return groups.filter((g) => g.parentId === parentId);
};

export const getRootGroups = (groups: PermissionGroup[]): PermissionGroup[] => {
  const groupIds = new Set(groups.map((g) => g.id));
  return groups.filter((g) => g.parentId === null || !groupIds.has(g.parentId));
};

export const getGroupPath = (groups: PermissionGroup[], groupId: string): PermissionGroup[] => {
  const path: PermissionGroup[] = [];
  let current = groups.find((g) => g.id === groupId);

  while (current) {
    path.unshift(current);
    current = current.parentId ? groups.find((g) => g.id === current!.parentId) : undefined;
  }

  return path;
};

export const getAncestorIds = (groups: PermissionGroup[], groupId: string): string[] => {
  const ids: string[] = [];
  let current = groups.find((g) => g.id === groupId);
  while (current?.parentId) {
    ids.push(current.parentId);
    current = groups.find((g) => g.id === current!.parentId);
  }
  return ids;
};

const isDescendantOf = (groups: PermissionGroup[], descendantId: string, ancestorId: string): boolean => {
  return getAncestorIds(groups, descendantId).includes(ancestorId);
};

// Toggle a group's selection while keeping the set canonical: no selected node
// may be an ancestor or descendant of another selected node.
export const toggleWithPruning = (
  groups: PermissionGroup[],
  current: ReadonlySet<string>,
  targetId: string,
): Set<string> => {
  const next = new Set(current);
  if (next.has(targetId)) {
    next.delete(targetId);
    return next;
  }
  // Remove any selected ancestors of the target.
  for (const ancestorId of getAncestorIds(groups, targetId)) {
    next.delete(ancestorId);
  }
  // Remove any selected descendants of the target.
  for (const id of [...next]) {
    if (isDescendantOf(groups, id, targetId)) next.delete(id);
  }
  next.add(targetId);
  return next;
};

// True if some selected id is a descendant of groupId.
export const hasSelectedDescendant = (
  groups: PermissionGroup[],
  selected: ReadonlySet<string>,
  groupId: string,
): boolean => {
  for (const id of selected) {
    if (id !== groupId && isDescendantOf(groups, id, groupId)) return true;
  }
  return false;
};

// True if some selected id is an ancestor of groupId (so groupId is implicitly covered).
export const hasSelectedAncestor = (
  groups: PermissionGroup[],
  selected: ReadonlySet<string>,
  groupId: string,
): boolean => {
  return getAncestorIds(groups, groupId).some((id) => selected.has(id));
};
